import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import Cookies from 'js-cookie';
import './Map.css';
import MapTable from './MapTable';
import DeviceConnection from './DeviceConnection';
import { googleMapEdit, googleMapKML } from '../settings'; // Import URLs from settings.js
import { useBle } from './BleContext'; // Ensure you have useBle to manage Bluetooth connections

const Map = () => {
  const [file, setFile] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoUpload, setAutoUpload] = useState(Cookies.get('autoUpload') === 'true');
  const [accumulatePoints, setAccumulatePoints] = useState(Cookies.get('accumulatePoints') === 'true');
  const [applyDate, setApplyDate] = useState(Cookies.get('applyDate') === 'true');
  const [applySorting, setApplySorting] = useState(Cookies.get('applySorting') === 'true');
  const [date, setDate] = useState(() => {
    const storedDate = Cookies.get('date');
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const formattedTomorrow = tomorrow.toISOString().slice(0, 10);
    if (!storedDate || new Date(storedDate) < new Date()) {
      Cookies.set('date', formattedTomorrow, { expires: 365 });
      return formattedTomorrow;
    }
    return storedDate;
  });
  const { requestMapData, uploadMapData } = useBle();
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [sentPoints, setSentPoints] = useState([]);
  const [connectionState, setConnectionState] = useState('❌ Not Connected');
  const [mapSource, setMapSource] = useState(''); // New state to track map source
  const [radius, setRadius] = useState(Cookies.get('radius') || 0); // New state to track radius

  const handleRadiusChange = (event) => {
    const value = event.target.value;
    setRadius(value);
    Cookies.set('radius', value, { expires: 365 }); // Save the radius value in cookies
  };

  const handleAccumulatePointsChange = (event) => {
    setAccumulatePoints(event.target.checked);
    Cookies.set('accumulatePoints', event.target.checked, { expires: 365 });
  };

  const handleApplyDateChange = (event) => {
    const checked = event.target.checked;
    setApplyDate(checked);
    Cookies.set('applyDate', checked, { expires: 365 });

    // Process existing points if the checkbox is set to true
    if (checked) {
      const updatedPoints = reprocessPointsWithDate(points, date);
      setPoints(updatedPoints);
    }
  };

  const handleApplySortingChange = (event) => {
    const checked = event.target.checked;
    setApplySorting(checked);
    Cookies.set('applySorting', checked, { expires: 365 });

    // Apply sorting to existing points if the checkbox is set to true
    if (checked) {
      const sortedPoints = sortPoints(points);
      setPoints(sortedPoints);
    }
  };

  const handleDateChange = (event) => {
    const value = event.target.value;
    setDate(value);
    Cookies.set('date', value, { expires: 365 });

    // Reprocess points if the "Apply date" checkbox is checked
    if (applyDate) {
      const updatedPoints = reprocessPointsWithDate(points, value);
      setPoints(updatedPoints);
    }
  };

  const reprocessPointsWithDate = (pointsToProcess, selectedDate) => {
    const newDate = selectedDate.slice(2).replace(/-/g, ''); // Extract YYMM from the selected date
    const updatedPoints = pointsToProcess.map((point) => {

      let startStr = point.start.toString().slice(-4).padStart(4, '0'); // Ensure start time has at least 4 digits
      let endStr = point.end.toString().slice(-4).padStart(4, '0'); // Ensure end time  has at least is 4 digits

      console.log(startStr,endStr);
      // If the original end time is '0000' (or doesn't exist), apply 23:59 as the default
      if (endStr === '0000') {
        endStr = newDate + '2359';
      } else {
        endStr = newDate + endStr;
      }

      // Create updated start by combining new date with original time portion
      startStr = newDate + startStr;

      return {
        ...point,
        start: parseInt(startStr, 10),
        end: parseInt(endStr, 10),
      };
    });

    return updatedPoints;
  };

  const sortPoints = (pointsToSort) => {
    const sortedPoints = [...pointsToSort];

    sortedPoints.sort((a, b) => {
      if (a.name.toLowerCase() === 'home' || a.id === '0') {
        a.interactivity = 1;
        return -1;
      }
      if (b.name.toLowerCase() === 'home' || b.id === '0'){
        b.interactivity = 1;
        return 1;
      } 
      if (a.end !== b.end) return a.end - b.end;
      if (a.start !== b.start) return a.start - b.start;
      return a.name.localeCompare(b.name);
    });

    return sortedPoints;
  };

  const handlePoints = (newPoints, forceAdd = false, accumulate = accumulatePoints) => {
    let updatedPoints = newPoints;

    if (applyDate) {
      updatedPoints = reprocessPointsWithDate(newPoints, date);
    }

    if (!accumulate && !forceAdd) {
      setPoints(updatedPoints);
    } else {
      setPoints((prevPoints) => {
        const finalPoints = [...prevPoints];

        updatedPoints.forEach((newPoint) => {
          const match = finalPoints.find((point) =>
            point.id === newPoint.id &&
            point.start === newPoint.start &&
            point.end === newPoint.end
          );
          if (!match || forceAdd) {
            finalPoints.push(newPoint);
          }
        });

        return finalPoints;
      });
    }

    // Apply sorting logic if the checkbox is checked
    if (applySorting) {
      setPoints((prevPoints) => sortPoints(prevPoints));
    }

    
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setError(''); // Reset error message before starting upload
      const response = await axios.post(`https://convert-kml-to-map-points-fzjaqefbta-uc.a.run.app?radius=${radius}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Response data:', response.data);
      handlePoints(response.data.data, false, accumulatePoints); // Pass accumulatePoints as argument
      setMapSource('file'); // Set map source to 'file' after uploading points
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAndUpload = async () => {
    try {
      setLoading(true);
      const response = await axios.get(googleMapKML, { responseType: 'blob' });
      const file = new Blob([response.data], { type: 'application/vnd.google-earth.kml+xml' });
      handleUpload(file);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download and upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setError(''); // Reset error message when a new file is selected
      handleUpload(selectedFile); // Automatically upload the file
    }
  }, [radius, accumulatePoints]); // Include accumulatePoints in dependency array

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/vnd.google-earth.kmz': ['.kmz'],
      'application/gpx+xml': ['.gpx']
    },
  });

  const handleFetchMapPoints = async () => {
    try {
      console.log('Requesting map data from device...');
      await requestMapData(
        (totalPoints) => {
          console.log('Starting map data transfer, total points:', totalPoints);
          setPoints([]); // Clear previous points before starting the transfer
        },
        (currentPoint, totalPoints, point) => {
          handlePoints([point], true, accumulatePoints); // Pass accumulatePoints as argument
        },
        (points) => {
          console.log('Map data received successfully:', points);
          handlePoints(points, false, accumulatePoints); // Pass accumulatePoints as argument
          setMapSource('compass'); // Set map source to 'compass' after reading points
        }
      );
    } catch (error) {
      console.error('Error requesting map data:', error);
      setError('Failed to request map data from the device. Please try again.');
    }
  };

  const handleUploadMap = async (uploadPoints) => {
    try {
      setSentPoints([]); // Reset sent points when uploading starts
      console.log('Uploading map data to device...');
      await uploadMapData(
        uploadPoints,
        (currentPoint, totalPoints) => {
          console.log(`Progress: ${currentPoint}/${totalPoints}`);
          setProgress({ current: currentPoint, total: totalPoints });
          setSentPoints((prevSentPoints) => [...prevSentPoints, currentPoint - 1]); // Add current point to sent points
        }
      );
      console.log('Map data uploaded successfully');
    } catch (error) {
      console.error('Error uploading map data:', error);
      setError('Failed to upload map data to the device. Please try again.');
    }
  };

  useEffect(() => {
    if (autoUpload && points.length === 0) {
      handleDownloadAndUpload();
    }
  }, [autoUpload]);

  const handleAutoUploadChange = (event) => {
    setAutoUpload(event.target.checked);
    Cookies.set('autoUpload', event.target.checked, { expires: 365 });
  };

  const handleDeviceConnect = () => {
    console.log("handleDeviceConnect");
    setSentPoints([]); // Clear memory of uploaded points when device connects or reconnects
    if (autoUpload && points.length > 0 && mapSource !== 'compass') {
      handleUploadMap(points); // Pass the current points explicitly
    }
  };

  const handleDeviceDisconnect = () => {
    setSentPoints([]); // Clear memory of uploaded points when device disconnects
  };

  const handleCleanMap = () => {
    setPoints([]);
    setFile(null);
    setError('');
  };

  return (
    <div className="map-upload">
      <DeviceConnection
        onConnect={handleDeviceConnect}
        onDisconnect={handleDeviceDisconnect}
        onConnectionStateChange={setConnectionState}
      />
      {connectionState === '✅ Connected' && (
        <div className="device-buttons">
          <button onClick={handleFetchMapPoints} className="active-button" disabled={loading}>
            {loading ? 'Reading...' : 'Read map from compass'}
          </button>
          <button
            onClick={() => handleUploadMap(points)} // Pass the current points explicitly
            className="active-button"
            disabled={loading}
          >
            {loading ? 'Uploading...' : 'Upload map to compass'}
          </button>
        </div>
      )}

      <p>
        <label>
          <input type="checkbox" checked={autoUpload} onChange={handleAutoUploadChange} />
          Upload map automatically
        </label>
      </p>
      <p>
        <label>
          <input type="checkbox" checked={accumulatePoints} onChange={handleAccumulatePointsChange} />
          Accumulate points
        </label>
      </p>
      <p>
        <label>
          <input type="checkbox" checked={applyDate} onChange={handleApplyDateChange} />
          Apply date
        </label>
      </p>
      <p>
        <label>
          <input type="checkbox" checked={applySorting} onChange={handleApplySortingChange} />
          Apply sorting
        </label>
      </p>
      <p>
        <label>
          Date:
          <input
            type="date"
            value={date}
            onChange={handleDateChange}
          />
        </label>
      </p>

      <p>
        <button onClick={handleCleanMap}>
          Clean map
        </button>
      </p>
      
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        {file ? (
          <p>{file.name}</p>
        ) : (
          <p>Drag 'n' drop a KMZ/KML/GPX file here, or click to select one</p>
        )}
      </div>
      
      <p>
        <label>
          Radius:
          <input
            type="number"
            value={radius}
            onChange={handleRadiusChange}
            min="0"
            step="1"
          />
        </label>
      </p>
      
      {error && <p className="error-message">{error}</p>}

      <p>
        <button onClick={handleDownloadAndUpload} className="active-button" disabled={loading}>
          {loading ? 'Downloading...' : 'Download default map'}
        </button> <br/>
        <a href={googleMapEdit} target="_blank" rel="noopener noreferrer">
          Edit default map
        </a>
      </p>
      <div className="map-table-container">
        <MapTable 
          points={points}
          progress={progress}
          sentPoints={sentPoints}
        />
      </div>
    </div>
  );
};

export default Map;
